/**
 * 应用外壳 —— 🔴 **Web 骨架**（O 域 `UI工程移交文档_Web端` §5.3 / §9 · `DR-025`）
 *
 * ```
 * ┌──────────┬────────────────────────────────────────┐
 * │ 侧栏 228 │ 顶栏 58（页面标题 · 记一笔 · 主题 · 退出）│
 * │  ├ 品牌   ├────────────────────────────────────────┤
 * │  ├ 账本   │ 内容区（上限 1320px · 居中 · 页边距自适应）│
 * │  ├ 三组   │                                        │
 * │  └ 信任   │                                        │
 * └──────────┴────────────────────────────────────────┘
 * ```
 *
 * ## 🔴 为什么是「侧栏」而不是「底部 tab bar」（2026-10-03 定 · 本批改正）
 *
 * `UI工程移交文档_Web端` **§9「小程序端映射」**那张端别表把两者分得很清楚：
 *
 * | 项 | Web | 小程序 |
 * |---|---|---|
 * | 侧栏 | **228px 固定侧栏** | 无侧栏，改底部 tab bar |
 * | 导航 | **3 组 10 项**（`DR-025`） | 需裁剪为 4–5 个 tab |
 *
 * ⑳ 批做外壳时取了**右边那一列**（`Container maxWidth="sm"` + `BottomNavigation`），
 * 而 `packages/client` 是 **Web 端**（架构 §10.2）—— 即「小程序骨架装在了 Web 上」，
 * 且**从未登记**（全库搜「移动优先 / mobile-first」= 0 处）。本批按设计改正，
 * 底栏骨架**归还小程序**（`packages/miniprogram` 尚未开工，只有 token 产物）。
 *
 * ## 🔴 三条纪律
 * 1. **尺寸/颜色一律走令牌**（`theme.jiaying.*`）—— 本文件不出现 `#xxxxxx` 或设计 px 字面量；
 * 2. **导航清单只在 `nav.tsx`**，本文件只渲染；**未实现的域不画灰项**（点不动比没有更糟）；
 * 3. **折叠靠 CSS 媒体查询**（设计就是这么做的：`@media (max-width: 1024px)` 收成 64px 图标条），
 *    不引入 JS 状态 —— 于是端侧测试（`renderToStaticMarkup`）拿到的 DOM 是**确定的**。
 */

import { Alert, Box, Button, IconButton, Menu, MenuItem, Snackbar, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AddOutlined,
  DarkModeOutlined,
  LightModeOutlined,
  LogoutOutlined,
  SettingsOutlined,
  SwapHorizOutlined,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ThemeValue } from '@jiaying/shared';
import { useApp } from '../app/store.js';
import { listLedgerMembers, listLedgers, updatePreferences } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { implementedGroups, titleOf, type NavItem } from './nav.js';
import { resolveShortcut } from './keymap.js';
// 🆕 实时提示条（批次 `66`）—— 架构 §7.2「顶部轻提示条」
import { RealtimeToast } from '../ui/RealtimeToast.js';

/** 设计里唯一的响应式断点：`≤1024px` 侧栏收成 64px 图标条（`app.css` 的同名媒体查询） */
const COLLAPSED = '@media (max-width: 1024px)';

/** 折叠时**隐藏文字**（与设计 `app.css` 行 743 隐藏的元素一一对应） */
const HIDE_WHEN_COLLAPSED = { [COLLAPSED]: { display: 'none' } };

/* ───────────────────────── 品牌标 ─────────────────────────
   移植自设计资产 `assets/_chrome.js` 的 `logo()`：
   一个屋檐（家）两条线共脊 + 下方上扬账线（财务）+ 两个错位窗点（协作成员）。
   ⚠️ 设计写 `border-radius: 9px`，令牌无 9px 档 ⇒ 取最近档 `r-md`（10px，肉眼不可辨）。 */

function BrandMark() {
  const tk = useTheme().jiaying.tokens;
  // 🔴 色值必须来自令牌（规则 `no-forbidden-legacy`）：logo 画在**品牌色渐变**上
  //    ⇒ 用「反色」令牌 `c-text-invert`（语义正好：品牌底上的前景色）。
  //    设计资产 `_chrome.js` 里写的是字面量 `#fff` —— 那是原型（无 token 层），移植时必须换。
  const invert = tk['c-text-invert'];
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        flex: '0 0 32px',
        borderRadius: tk['r-md'],
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
        background: `linear-gradient(145deg, ${tk['em-400']}, ${tk['em-700']})`,
      }}
    >
      <Box
        component="svg"
        width={32}
        height={32}
        viewBox="0 0 32 32"
        fill="none"
        role="img"
        aria-label="家盈"
        sx={{ display: 'block' }}
      >
        <path d="M5 15.2 16 6.5l11 8.7" stroke={invert} strokeWidth={2.1} strokeLinecap="round" strokeLinejoin="round" />
        <path
          d="M8.2 14.4v10.4a1.6 1.6 0 0 0 1.6 1.6h12.4a1.6 1.6 0 0 0 1.6-1.6V14.4"
          stroke={invert}
          strokeOpacity={0.62}
          strokeWidth={1.7}
          strokeLinecap="round"
        />
        <path d="M11 24.6l3.2-3.4 2.6 2.2 4.2-4.8" stroke={invert} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12.4" cy="18.6" r="1.15" fill={invert} opacity={0.9} />
        <circle cx="19.6" cy="18.6" r="1.15" fill={invert} opacity={0.62} />
      </Box>
    </Box>
  );
}

/* ───────────────────────── 侧栏一段：账本切换 ───────────────────────── */

function LedgerSwitch() {
  const tk = useTheme().jiaying.tokens;
  const { session, setLedger } = useApp();
  const ledgers = useQuery({ queryKey: ['ledgers'], queryFn: listLedgers });
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  // ⚠️ 当前账本名从**服务端列表**里取（不依赖会话里是否顺手存了名字 —— 少一个可能不同步的副本）
  const current = ledgers.data?.find((l) => l.id === session?.ledgerId);
  const memberText = current === undefined ? '' : `${String(current.memberCount)} 位成员`;

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e: ReactMouseEvent<HTMLElement>) => {
          setAnchor(e.currentTarget);
        }}
        disabled={ledgers.data === undefined || ledgers.data.length === 0}
        sx={{
          mx: '12px',
          mt: '4px',
          mb: '12px',
          px: '10px',
          py: '7px',
          width: 'calc(100% - 24px)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          textAlign: 'left',
          cursor: 'pointer',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: tk['r-md'],
          bgcolor: tk['c-surface-sunk'],
          color: 'inherit',
          font: 'inherit',
          transition: `border-color ${tk['t-fast']}, background ${tk['t-fast']}`,
          '&:hover': { borderColor: tk['c-border-strong'], bgcolor: 'background.paper' },
          [COLLAPSED]: { justifyContent: 'center', p: '7px' },
        }}
      >
        <Box sx={{ width: 7, height: 7, flex: '0 0 7px', borderRadius: '50%', bgcolor: 'primary.main' }} />
        <Box sx={{ flex: 1, minWidth: 0, ...HIDE_WHEN_COLLAPSED }}>
          <Box
            component="b"
            sx={{
              display: 'block',
              fontSize: tk['fs-12'],
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {current?.name ?? ''}
          </Box>
          <Box component="span" sx={{ display: 'block', fontSize: tk['fs-11'], color: 'text.secondary' }}>
            {memberText}
          </Box>
        </Box>
        <SwapHorizOutlined sx={{ fontSize: 14, flex: '0 0 14px', color: 'text.secondary', ...HIDE_WHEN_COLLAPSED }} />
      </Box>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => {
          setAnchor(null);
        }}
      >
        {(ledgers.data ?? []).map((l) => (
          <MenuItem
            key={l.id}
            selected={l.id === session?.ledgerId}
            onClick={() => {
              setLedger(l.id, l.name);
              setAnchor(null);
            }}
          >
            {l.name}
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {l.memberCount} 人
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/* ───────────────────────── 侧栏一段：导航（三组） ───────────────────────── */

function NavRow({ item, active }: { item: NavItem; active: boolean }) {
  const tk = useTheme().jiaying.tokens;
  return (
    <Box
      component={NavLink}
      to={item.path ?? '#'}
      // 折叠态文字被 CSS 隐藏 ⇒ `title` 是那时唯一的可读标识（展开态冗余一点，换取折叠态可用）
      title={item.label}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        px: '10px',
        py: '8px',
        borderRadius: tk['r-md'],
        color: active ? tk['em-700'] : 'text.secondary',
        fontSize: tk['fs-13'],
        fontWeight: active ? 600 : 500,
        textDecoration: 'none',
        bgcolor: active ? tk['c-brand-weak'] : 'transparent',
        '&:hover': { bgcolor: tk['c-surface-sunk'], color: 'text.primary' },
        // 设计：`@media (max-width:1024px) { .nav-item { justify-content: center; } }`
        [COLLAPSED]: { justifyContent: 'center', px: '6px' },
      }}
    >
      <Box
        component="span"
        sx={{ display: 'grid', placeItems: 'center', width: 17, height: 17, flex: '0 0 17px', opacity: active ? 1 : 0.85 }}
      >
        {item.icon}
      </Box>
      <Box component="span" sx={{ ...HIDE_WHEN_COLLAPSED }}>
        {item.label}
      </Box>
    </Box>
  );
}

function SideNav() {
  const tk = useTheme().jiaying.tokens;
  const { pathname } = useLocation();
  const groups = implementedGroups();

  return (
    <Box component="nav" sx={{ px: '10px', pb: '12px' }}>
      {groups.map((g) => (
        <Box key={g.group}>
          <Box
            sx={{
              fontSize: tk['fs-11'],
              fontWeight: 600,
              color: 'text.secondary',
              letterSpacing: '.04em',
              px: '8px',
              pt: '12px',
              pb: '5px',
              ...HIDE_WHEN_COLLAPSED,
            }}
          >
            {g.group}
          </Box>
          {g.items.map((it) => (
            <NavRow key={it.key} item={it} active={pathname === it.path} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

/* ───────────────────────── 侧栏（四段） ───────────────────────── */

function Sidebar() {
  const tk = useTheme().jiaying.tokens;
  return (
    <Box
      component="aside"
      sx={{
        width: tk['sidebar-w'],
        flex: `0 0 ${tk['sidebar-w']}`,
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        [COLLAPSED]: { width: tk['sidebar-wc'], flex: `0 0 ${tk['sidebar-wc']}` },
      }}
    >
      {/* ① 品牌 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          px: '18px',
          pt: '18px',
          pb: '14px',
          [COLLAPSED]: { justifyContent: 'center', px: '10px' },
        }}
      >
        <BrandMark />
        <Box sx={{ ...HIDE_WHEN_COLLAPSED }}>
          <Box
            component="span"
            sx={{ display: 'block', fontSize: tk['fs-15'], fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.01em' }}
          >
            家盈
          </Box>
          <Box component="span" sx={{ display: 'block', fontSize: tk['fs-11'], color: 'text.secondary', lineHeight: 1.3, mt: '1px' }}>
            家庭财务中控台
          </Box>
        </Box>
      </Box>

      {/* ② 账本切换（全局上下文：改它 = 改所有页的数据范围） */}
      <LedgerSwitch />

      {/* ③ 三组导航 */}
      <SideNav />

      {/* ④ 信任承诺（常驻可见 —— 产品公开承诺，`_chrome.js` 的 `.trust-chip`） */}
      <Box sx={{ mt: 'auto', p: '12px', borderTop: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '7px',
            px: '10px',
            py: '8px',
            borderRadius: tk['r-md'],
            bgcolor: tk['c-brand-weak'],
            border: `1px solid ${tk['c-brand-line']}`,
            fontSize: tk['fs-11'],
            color: tk['em-700'],
            lineHeight: 1.35,
            [COLLAPSED]: { justifyContent: 'center', px: '6px' },
          }}
        >
          <VerifiedUserOutlined sx={{ fontSize: 14, flex: '0 0 14px' }} />
          <Box component="span" sx={{ ...HIDE_WHEN_COLLAPSED }}>
            无广告 · 无导流 · 导出免费
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* ───────────────────────── 顶栏一段：成员头像堆叠 ───────────────────────── */

/**
 * 顶栏右侧的**成员头像堆叠**（设计 `_chrome.js` 的 `.avatar-stack`）。
 *
 * ## 🔴 为什么它属于「顶栏」而不是侧栏
 * 侧栏四段（品牌 / 账本切换 / 三组导航 / 信任承诺）里**没有**"谁在这个账本里"这一层，
 * 而"这是个**家庭**账本"是产品的第一性事实 —— 设计把它放在顶栏右侧、设置齿轮**之前**
 * （`_chrome.js:181-186`）。
 *
 * ## 🔴 数据源唯一
 * `GET /api/ledgers/members`（L 域 · `AC-L6.1` owner 与 member 逐行相同）。
 * 设置页的「成员」面板读**同一个 queryKey** ⇒ 两处不会各自拿一份、也不会漂移。
 *
 * ## 头像颜色
 * 设计的第二个头像起换底色（`.a-2` / `.a-3` 蓝 / 橙）⇒ 按**下标**取设计色板。
 * 🔴 但**绝不能靠"我记得第几个是蓝的"** —— 那是对 CSS 的口述复述。
 * 本批只取令牌 `jiaying.chart[]`（`theme.ts` 里由 `--cat-1..8` 生成，已是设计色板），
 * 下标越界就退回品牌渐变 —— **同色两人也能靠 `title` 区分**，不靠颜色认人。
 */
function MemberAvatars() {
  const tk = useTheme().jiaying;
  const { session } = useApp();
  const members = useQuery({
    queryKey: ['ledgerMembers'],
    queryFn: listLedgerMembers,
    // 🔴 没有账本上下文时不发请求（`/api/ledgers/members` 需要 `X-Ledger-Id`）
    enabled: session !== null,
  });

  const items = members.data ?? [];
  if (items.length === 0) return null;

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}
      data-testid="topbar-avatar-stack"
      title={items.map((m) => m.name).join(' · ')}
    >
      {items.slice(0, 3).map((m, i) => {
        // 🔴 取名首字；空名/纯空白（服务端允许 `name` 为空？）退化成 `?` —— 画空头像比画错字好
        const initial = (m.name.trim()[0] ?? '?').toUpperCase();
        return (
          <Box
            key={m.userId}
            title={`${m.name}${m.isSelf ? '（我）' : ''}`}
            sx={{
              width: 30,
              height: 30,
              borderRadius: tk.tokens['r-full'],
              display: 'grid',
              placeItems: 'center',
              fontSize: tk.tokens['fs-12'],
              fontWeight: 600,
              color: tk.tokens['c-text-invert'],
              // 设计：首个用品牌渐变，其余从色板取；下标越界回落到品牌渐变（不抛错）
              background:
                i === 0 || tk.chart[i] === undefined
                  ? `linear-gradient(145deg, ${tk.tokens['em-400']}, ${tk.tokens['em-700']})`
                  : tk.chart[i],
              // 设计的 `.avatar-stack .avatar` 用负 margin 叠压 + 2px 背景色描边
              ml: i === 0 ? 0 : '-7px',
              border: `2px solid ${tk.tokens['c-bg']}`,
              boxSizing: 'border-box',
            }}
          >
            {initial}
          </Box>
        );
      })}
      {/* 超过 3 人时给「还有 N 位」—— 头像堆叠不能让人误以为全家就这几个人 */}
      {items.length > 3 && (
        <Box
          sx={{
            width: 30,
            height: 30,
            ml: '-7px',
            borderRadius: tk.tokens['r-full'],
            display: 'grid',
            placeItems: 'center',
            fontSize: tk.tokens['fs-11'],
            border: `2px solid ${tk.tokens['c-bg']}`,
            bgcolor: 'background.paper',
            color: 'text.secondary',
            boxSizing: 'border-box',
          }}
        >
          +{items.length - 3}
        </Box>
      )}
    </Box>
  );
}

/* ───────────────────────── 顶栏 ───────────────────────── */

function Topbar({ actions }: { actions?: ReactNode }) {
  const tk = useTheme().jiaying.tokens;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { session, logout, themeMode, themePreference, toggleTheme, setThemePreference } = useApp();

  /**
   * 🔴 **顶栏切换必须落库**（批次 `94`）。
   *
   * 修前只 `dispatch`（改本地）不调接口 ⇒ 刷新一次就被服务端旧值覆盖回去。
   * 用户看到的是"切了，但下次进来又是暗色"，与"根本切不动"在体感上没区别。
   *
   * ⚠️ 失败要**回滚本地**：界面亮着、库里还是旧值，就是最典型的一种静默不一致
   * （下次刷新才发现）。回滚 + 一条明确报错，比"看起来成功了"诚实。
   */
  const themeBeforeToggle = useRef<ThemeValue>(themePreference);
  const [themeError, setThemeError] = useState<string | null>(null);
  const saveTheme = useMutation({
    mutationFn: (theme: ThemeValue) => updatePreferences({ theme }),
    onSuccess: () => {
      // 与设置页面板共用同一个 queryKey ⇒ 面板里的选中态同步跟上（一处写、两处读）
      void qc.invalidateQueries({ queryKey: ['preferences'] });
    },
    onError: (e: unknown) => {
      setThemePreference(themeBeforeToggle.current);
      setThemeError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const onToggleTheme = (): void => {
    themeBeforeToggle.current = themePreference;
    const next: ThemeValue = themeMode === 'dark' ? 'light' : 'dark';
    setThemeError(null);
    toggleTheme(); // 先动界面（主题切换不该等一次网络往返）
    saveTheme.mutate(next);
  };

  return (
    <Box
      component="header"
      sx={{
        height: tk['topbar-h'],
        flex: `0 0 ${tk['topbar-h']}`,
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        px: tk['pad-page'],
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        position: 'sticky',
        top: 0,
        zIndex: 40,
      }}
    >
      <Box sx={{ fontSize: tk['fs-16'], fontWeight: 600, letterSpacing: '-0.01em', minWidth: 0 }}>
        {titleOf(pathname)}
      </Box>
      <Box sx={{ flex: 1 }} />

      {actions}

      {/* 🔴 「记一笔」= 主操作。设计里它在页面 `actionbar` 内（配 `N` 快捷键，`AC-O2.8`）；
          底栏摘除后必须有**全局入口** ⇒ 提为顶栏主操作。⚠️ 差异已登记（交接文档 §6.3）。 */}
      <Button
        variant="contained"
        size="small"
        startIcon={<AddOutlined />}
        onClick={() => {
          navigate('/new');
        }}
        sx={{ whiteSpace: 'nowrap' }}
      >
        记一笔
      </Button>

      <Tooltip title={themeMode === 'dark' ? '切到亮色' : '切到暗色'}>
        <IconButton
          onClick={onToggleTheme}
          aria-label="切换主题"
          size="small"
          data-testid="topbar-theme-toggle"
        >
          {themeMode === 'dark' ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* 主题保存失败 ⇒ 明确说出来（本地已回滚，别让用户以为切成功了） */}
      <Snackbar
        open={themeError !== null}
        autoHideDuration={4000}
        onClose={() => {
          setThemeError(null);
        }}
      >
        <Alert severity="error" data-testid="topbar-theme-error">
          外观没保存成功：{themeError ?? ''}
        </Alert>
      </Snackbar>

      {/* 🆕 成员头像堆叠（设计 `_chrome.js:181-183`）—— 数据源 `GET /api/ledgers/members`（L 域已就绪） */}
      <MemberAvatars />

      {/*
        🔴 **设置入口**（`AC-O1.6`：设置入口在顶栏、**不占导航位**；N 域 PRD §5.1 入口 = 顶栏右上「头像 / 齿轮」）。
        设计把齿轮放在**最右**（`_chrome.js:184`）—— 本批照此位置。
        ⚠️ 设计的顶栏右侧完整顺序 = 搜索 → 主题 → 通知 → 头像 → **设置**。
        **搜索与通知铃本批不画**：服务端无对应能力（P0 无检索聚合接口、无提醒域），
        按「不画灰项」纪律 —— 一个点不动的铃铛比没有更糟（它承诺了一件产品做不到的事）。
      */}
      <Tooltip title="家庭与设置">
        <IconButton
          onClick={() => {
            navigate('/settings');
          }}
          aria-label="家庭与设置"
          size="small"
          data-testid="topbar-settings-button"
        >
          <SettingsOutlined fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* ⚠️ 退出的位置：设计里应在**头像下拉内**；P0 无头像菜单 ⇒ 暂以独立图标呈现（差异已登记） */}
      <Tooltip title={`退出（${session?.userName ?? ''}）`}>
        <IconButton
          onClick={logout}
          aria-label="退出登录"
          size="small"
        >
          <LogoutOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/* ───────────────────────── 外壳装配 ───────────────────────── */

export function Shell() {
  const tk = useTheme().jiaying.tokens;
  const navigate = useNavigate();
  // 🆕 提示条状态（批次 `66`）—— 订阅点 `useRealtime` 喂它，这里只渲染
  const { realtimeToast, clearRealtimeToast } = useApp();

  /**
   * 🔴 **Web 键盘流**（`AC-O2.8`）：`N` = 记一笔 · **数字键 `1`–`9` = 导航**（本批新增）。
   *
   * ⚠️ 本 effect 只做**三件薄事**（规则全在 `keymap.ts` 的纯函数里 —— 那里能在单测直测）：
   *   ① 把 DOM `KeyboardEvent` 翻译成 `KeyPress`（纯数据）；
   *   ② 问 `resolveShortcut()` 该做什么；
   *   ③ 执行。
   *
   * 🔴 **为什么"翻译"这一步必须精确** —— `mod` 要合并 `metaKey` 与 `ctrlKey`：
   * 本机是 Windows（`ctrlKey`），但设计原文写的是 `Ctrl K`；Mac 上用户按的是 `⌘`。
   * 两者都算修饰键，**漏掉任一个**都会让"在复制文本时按了 `1`"跳到资产页。
   *
   * ⚠️ `useEffect` 在 SSR（`renderToStaticMarkup`）不执行 ⇒ 端侧静态渲染没有副作用；
   *     ⇒ **键盘行为只能靠无头浏览器取证**（见 §3 ㊷ 判据：几何/时序类结论单测证不了）。
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const next = resolveShortcut({
        key: e.key,
        mod: e.metaKey || e.ctrlKey,
        alt: e.altKey,
        target: e.target,
        defaultPrevented: e.defaultPrevented,
      });
      if (next.kind !== 'navigate') return;
      e.preventDefault();
      navigate(next.to);
    };
    globalThis.addEventListener('keydown', onKey);
    return () => {
      globalThis.removeEventListener('keydown', onKey);
    };
  }, [navigate]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Topbar />
        {/* 🆕 实时提示条（批次 `66`）—— 架构 §7.2「用户不在流水页 ⇒ **顶部**轻提示条」。
            🔴 位置就在顶栏之下、内容之上（`Topbar` 与 `main` 之间）：架构写的是「顶部」，
                而"紧贴顶栏"既满足"顶部"又不遮挡正文（`RealtimeToast` 自身是 sticky）。
            🔴 点「查看」= 跳流水页 —— 本组件**不自己导航**（受控），导航由这里给。 */}
        <RealtimeToast
          state={realtimeToast}
          onView={() => {
            clearRealtimeToast();
            navigate('/journal');
          }}
          onDismiss={clearRealtimeToast}
        />
        {/* 设计 `.content`：`padding: var(--pad-page); max-width: var(--maxw); margin: 0 auto` */}
        <Box component="main" sx={{ flex: 1, width: '100%', maxWidth: tk['maxw'], mx: 'auto', p: tk['pad-page'] }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
